/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */

import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ApiFilterRequest, ApiFiltersRequest } from '../api';
import { TablePage, TableSort } from '../models';

/**
 * Constructor options for the AbstractDataSource class.
 *
 */
interface AbstractDataSourceConstructor<T> {
    clientSideManager?: boolean;
    tablePage?: TablePage;
    observableFn: any;
    filters?: ApiFilterRequest[];
}

/**
 * Data object for the fetch$ Subject.
 *
 */
export interface FetchSubjectData {
    pageNumber?: number;
    pageSize?: number;
    sort?: TableSort[];
    filter?: ApiFilterRequest[];
}

/**
 * Abstract data source for fetching and managing data for a table or similar component.
 *
 */ export abstract class AbstractDataSource<T> {
    /**
     * Array of rows containing the fetched data.
     *
     */
    rows: T[] = [];

    /**
     * Observable for the rows array.
     *
     */
    rows$: Observable<T[]> = of([]);

    /**
     * Current table page information.
     *
     */
    tablePage: TablePage;

    /**
     * Observable for the loading state of the data source.
     *
     */
    isLoading: Observable<boolean>;
    /**
     * Subject for triggering data fetch.
     *
     */
    protected fetch$: Subject<FetchSubjectData> = new Subject<FetchSubjectData>();

    /**
     * Initial page number for pagination.
     *
     */
    protected initialPage: number;

    /**
     * Array of table sorting options.
     *
     */
    protected tableSort: TableSort[] = [];

    /**
     * Array of API filter requests.
     *
     */
    protected filter: ApiFilterRequest[] = [];

    /**
     * Observable function for fetching data.
     *
     */
    protected observable: any;

    protected _clientSideManager = false;
    protected _firstAPICall = true;

    /**
     * Subject for the loading state.
     *
     */
    private _loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    /**
     * Creates an instance of the AbstractDataSource class.
     * @param options - Constructor options for the data source.
     *
     */
    constructor(options: AbstractDataSourceConstructor<T>) {
        if (options.clientSideManager) {
            this._clientSideManager = options.clientSideManager;
        }
        this.tablePage = options.tablePage || new TablePage();
        this.observable = options.observableFn;
        this.filter = options.filters || [];
        this.initialPage = this.tablePage.pageNumber;
        this.isLoading = this._loading$.asObservable();
        this.reInitFetch();
    }

    /**
     * Sets a new observable function for fetching data.
     * @param observable - The new observable function.
     *
     */
    setObservable(observable: any): void {
        this.observable = observable;
    }

    /**
     * Triggers a data refresh by emitting an empty fetch event.
     *
     */
    refresh(): void {
        this.fetch$.next({});
    }

    reInitFetch(): void {
        this.rows$ = this.fetch$
            .pipe(
                switchMap((data: FetchSubjectData = {}) => {
                    this._startLoading();
                    this._setPageNumber(data.pageNumber);
                    this._setPageSize(data.pageSize);
                    this._setSort(data.sort);
                    this._setFilter(data.filter);
                    return this.getElements();
                }),
                catchError((err) => {
                    return throwError(() => {
                        const error = new Error(`ERRORS.${err.status}.MESSAGE`);
                        (error as any).cause = err.error?.message || err.message;
                        return error;
                    });
                }),
                tap({
                    next: () => {
                        this._firstAPICall = false;
                        this._loaded();
                    },
                    error: () => {
                        this._firstAPICall = false;
                        this._loaded();
                    }
                }),
                shareReplay()
            )
            .pipe(
                tap((rows: T[]) => {
                    return (this.rows = rows);
                })
            );
    }

    start(): void {
        this.fetch$.next({});
    }

    /**
     * Performs pagination by updating the table page information.
     * @param pageNumber - The page number.
     * @param pageSize - The page size.
     *
     */
    paging(pageNumber: number = this.tablePage.pageNumber, pageSize: number = this.tablePage.size): void {
        this.fetch$.next({ pageNumber, pageSize });
    }

    /**
     * Performs sorting by updating the table sort options.
     * @param sort - The table sort options.
     *
     */
    sorting(sort: TableSort[] = this.tableSort): void {
        this.fetch$.next({ sort });
    }

    /**
     * Performs filtering by updating the filter options.
     * @param filter - The filter options.
     * @param logicalOperator - The logical operator for filter requests.
     *
     */
    filtering(filter: ApiFilterRequest[] = this.filter): void {
        if (this.fetch$.closed) {
            this.reInitFetch();
        }
        this.fetch$.next({ filter, pageNumber: this.initialPage });
    }

    /**
     * Retrieves the elements from the data source.
     * @returns An Observable that emits the array of elements.
     *
     */
    protected getElements(): Observable<T[]> {
        const filterRequest = new ApiFiltersRequest({
            filters: this.filter
        });

        return this.observable(filterRequest).pipe(
            // @ts-ignore
            map((res) => res.result)
        );
    }

    /**
     * Sets the page number for pagination.
     * @param pageNumber - The page number.
     *
     */
    protected _setPageNumber(pageNumber: number = this.tablePage.pageNumber): void {
        this.tablePage.pageNumber = pageNumber;
    }

    /**
     * Sets the page size for pagination.
     * @param pageSize - The page size.
     *
     */
    protected _setPageSize(pageSize: number = this.tablePage.size): void {
        this.tablePage.size = pageSize;
    }

    /**
     * Sets the sorting options for the table.
     * @param sort - The sorting options.
     *
     */
    protected _setSort(sort: TableSort[] = this.tableSort): void {
        this.tableSort = sort;
    }

    /**
     * Sets the filter options for the data source.
     * @param filter - The filter options.
     *
     */
    protected _setFilter(filter: ApiFilterRequest[] = this.filter): void {
        this.filter = filter;
    }

    /**
     * Starts the loading state by emitting a true value.
     *
     */
    protected _startLoading(): void {
        this._loading$.next(true);
    }

    /**
     * Ends the loading state by emitting a false value.
     *
     */
    protected _loaded(): void {
        this._loading$.next(false);
    }
}
